import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import { comnEnvs, comnUtils } from "@/comn/utils";
import { lpcoEnvs, lpcoUtils } from "@/tz/go/egaz/osci/lpco/comn"; // 시스템 공통 유틸
import { Page, Group, Layout, Button, Grid, ExcelUpload, FormControl, Table } from "@/comn/components";
import {
    useForm,
    useToast,
    useFetch,
    useModal,
    useAuth,
    useStore,
    useGrid,
    usePopup,
    useResource,
    useExcel,
    TGridSchema,
    usePage,
} from "@/comn/hooks";
import {
    BASE,
    URLS,
    APIS,
    SF_LPCO_INFO_R,
    SG_COND_PER_DOC_LIST,
    SG_COND_PER_WG_FOB_LIST,
    SG_COND_RNGE_WG_FOB_LIST,
    SG_COND_PER_CNTR_LIST,
    SG_COND_PCT_FOB_LIST,
    SG_COND_MAX_FOB_LIST,
    SG_GFS_CD_LST,
} from "./services/OsciCfgnFeeService";
import { CommonErrors } from "@/comn/components";

/*
 * @ 화면 컴포넌트 주석
 * @ 메타 시스템을 참고 하여 표준 단어와 용어를 사용하여 작성
 * @ !== ... ==! 치환 부호는 개발 완료 후 한글 주석을 인지하여 제거하기 위한 표시
 * @ 기본 여러줄의 주석 형태를 사용
 * @ 포맷
 * @ [화면명 영문]
 * @ [지환부호시작] [화면명 한글] [치환부호종료]
 *
 * 화면명 영문
 * !== 화면명 한글 ==!
 */

/*
 * Register LPCO Component
 * !== LPCO Component 등록 ==!
 */
export const OSCI0203023S = () => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_osci0004"],
    });

    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const { pgeStore, setStore, getStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const auth = useAuth(); // Login , User Information Hook !== 로그인, 유저정보 Hook ==!
    const { feeImpsCtraId } = useParams(); // Key Information of this Component Router !== 라우터에 정의된 키정보 ==!
    const { openPopup } = usePopup();
    const [lpcoFeeList, setLpcoFeeList] = useState(comnUtils.getGridData([]));
    // GFS Code List !== GFS 코드 목록 ==!
    const [gfsCdList, setGfsCdList] = useState(comnUtils.getGridData([]));

    const [feeImpsCtraCd, setFeeImpsCtraCd] = useState("");
    const [feeImpsCndtCd, setFeeImpsCndtCd] = useState("");

    const refSgFeeList = useRef<TGridSchema | null>();

    const refDataFeeList = useRef([]);

    const [stateLangCd, setStateLangCd] = useState(localStorage.getItem("lang")?.toUpperCase() || "EN");

    // ORG LIST
    const [orgCdList, setOrgCdList] = useState([]);
    // ORG LPCO LIST
    const [orgLpcoList, setOrgLpcoList] = useState([]);

    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "OSC0060" },
            { area: "comnCd", comnCd: "OSC0135" },
            { area: "comnCd", comnCd: "OSC0136" },
            { area: "comnCd", comnCd: "OSC0137" },
            { area: "comnCd", comnCd: "OSC0138" },
            { area: "comnCd", comnCd: "OSC0123" },
            { area: "comnCd", comnCd: "OSC0113" },
            { area: "comnCd", comnCd: "OSC0143" },
        ],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Register LPCO Component !== LPCO Component 등록 ==!
        lpcoFeeDtl: useForm({
            defaultSchema: SF_LPCO_INFO_R,
            defaultValues: {},
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     *
     */
    const grid = {
        // Component List !== Component 목록 ==!
        feeCondPerDocList: useGrid({
            defaultSchema: SG_COND_PER_DOC_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
        // GFS Code List !== GFS 코드 목록 ==!
        gfsCdList: useGrid({
            defaultSchema: SG_GFS_CD_LST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    const render = {
        grid_feeCondList: {
            head: {},
            cell: {},
            edit: {},
        },
        grid_gfsCdList: {
            head: {},
            cell: {},
            edit: {},
        },
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        getFeeInfo: useFetch({
            api: (data) => {
                return APIS.getFeeInfo(data);
            },
            onSuccess: (data) => {
                console.log("getFeeInfo onSuccess ==================== ", data);
                const feeMainInfo = data?.feeInfo?.content?.info || {};
                form.lpcoFeeDtl.setValues(feeMainInfo);

                const feeDtlList = data?.feeInfo?.content?.feeList || [];

                const feeImpsCtraCd = feeMainInfo["feeImpsCtraCd"];
                const feeImpsCndtCd = feeMainInfo["feeImpsCndtCd"];

                if (feeImpsCndtCd == "RA") {
                    let rngeTxt = "<= (Weight(KG) or FOB/CIF) <";
                    if (feeImpsCtraCd == "W") {
                        rngeTxt = "<= Weight(KG) <";
                    } else if (feeImpsCtraCd == "F") {
                        rngeTxt = "<= FOB <";
                    } else if (feeImpsCtraCd == "I") {
                        rngeTxt = "<= CIF <";
                    }
                    for (const rowData of feeDtlList) {
                        rowData["rngeTxt"] = rngeTxt;
                    }
                }

                refDataFeeList.current = feeDtlList;

                // GFS Code List !== GFS 코드 목록 ==!
                setGfsCdList(comnUtils.getGridData(data?.feeInfo?.content?.gfsList || []));

                setFeeImpsCtraCd(feeImpsCtraCd);
                setFeeImpsCndtCd(feeImpsCndtCd);
            },
            onError: () => {},
            showToast: false,
        }),
        deleteFeeInfo: useFetch({
            api: (feeImpsCtraId) => APIS.deleteFeeInfo(feeImpsCtraId),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.osc.00071",
                    onConfirm: () => {
                        navigate(URLS.osci0203022q);
                    },
                    onCancel: () => {
                        navigate(URLS.osci0203022q);
                    },
                });
            },
            onError: (err) => {
                modal.openModal({ content: err.response.data.errorMessage });
            },
            showToast: true,
        }),
    };
    /*
     * 화면에서 사용하는 이벤트, 함수를 선언
     * 기본 변수명은 메타 단어 조합 카멜 표기법을 따름
     * fetch 를 호출 하는 메소드: fetch 명과 동일하게 작성
     * 버튼이나 그리드의 이벤트의 경우 : [이벤트종류]_[이벤트대상타입]_[이벤트대상명]
     * 이벤트 대상 타입 : Btn , Grid ... 카멜표기법으로 표시, 첫글자 대문자
     * 이벤트 명 : 카멜표기법으로 표시, 첫글자 대문자
     *
     * const handler = {
     *      [handler 이름] : ()=>{ 함수 },
     *      click_Btn_Save : 저장버튼 클릭
     *      click_Grid_RpckItmAppList : 재포장 품목 신청서 그리드 클릭
     *      ...
     * }
     */
    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */

    const handler = {
        setFeeInfo: (feeImpsCtraId: any) => {
            fetch.getFeeInfo.fetch(feeImpsCtraId);
        },
        deleteFeeInfo: (feeImpsCtraId: any) => {
            modal.openModal({
                content: "msg.osc.00095",
                onConfirm: () => {
                    fetch.deleteFeeInfo.fetch(feeImpsCtraId);
                },
            });
        },
    };

    // useEffect
    useEffect(() => {
        handler.setFeeInfo(feeImpsCtraId);
        grid.feeCondPerDocList.setOption("edit", false);
        // GFS 코드 목록 : 조회 전용
        grid.gfsCdList.setOption("edit", false);
        grid.gfsCdList.setOption("radio", false);
    }, []);

    useEffect(() => {
        console.log("useEffect [feeImpsCtraCd, feeImpsCndtCd]", feeImpsCtraCd, feeImpsCndtCd);
        if (feeImpsCtraCd == "D" && feeImpsCndtCd == "PE") {
            // doc base + per
            refSgFeeList.current = SG_COND_PER_DOC_LIST;
        } else if ((feeImpsCtraCd == "I" || feeImpsCtraCd == "F" || feeImpsCtraCd == "W") && feeImpsCndtCd == "PE") {
            // fob + per
            refSgFeeList.current = SG_COND_PER_WG_FOB_LIST;
        } else if ((feeImpsCtraCd == "I" || feeImpsCtraCd == "F" || feeImpsCtraCd == "W") && feeImpsCndtCd == "RA") {
            // fob or weight + range
            refSgFeeList.current = SG_COND_RNGE_WG_FOB_LIST;
        } else if (feeImpsCtraCd == "C" && feeImpsCndtCd == "PE") {
            // container + per
            refSgFeeList.current = SG_COND_PER_CNTR_LIST;
        } else if ((feeImpsCtraCd == "I" || feeImpsCtraCd == "F") && feeImpsCndtCd == "PC") {
            // fob + percent
            refSgFeeList.current = SG_COND_PCT_FOB_LIST;
        } else if ((feeImpsCtraCd == "I" || feeImpsCtraCd == "F") && feeImpsCndtCd == "MX") {
            // fob + maximum
            refSgFeeList.current = SG_COND_MAX_FOB_LIST;
        } else {
            // others
            refSgFeeList.current = SG_COND_PER_DOC_LIST;
        }

        grid.feeCondPerDocList.setSchema(refSgFeeList.current);
        grid.feeCondPerDocList.setOption("edit", false);
        grid.feeCondPerDocList.setOption("checkbox", false);
        setLpcoFeeList(comnUtils.getGridData(refDataFeeList.current)); // -- RE-RENDERING
    }, [feeImpsCtraCd, feeImpsCndtCd]);

    return (
        <Page>
            <form>
                <Group>
                    <Group.Body>
                        <Group.Title title={"T_FEE_CLCU_INFO"} titleSize={2}></Group.Title>
                        <Group.Section>
                            <Group.Row>
                                <Group.Control {...form.lpcoFeeDtl.schema.orgNm}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.lpcoFeeDtl.schema.appNm}></Group.Control>
                                <Group.Control {...form.lpcoFeeDtl.schema.appTpCd}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.lpcoFeeDtl.schema.lpcoAplyTpCd}></Group.Control>
                                <Group.Control {...form.lpcoFeeDtl.schema.cmbiBllYn}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.lpcoFeeDtl.schema.feeRuleNm}></Group.Control>
                                <Group.Control {...form.lpcoFeeDtl.schema.feeImpsTpCd}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.lpcoFeeDtl.schema.feeImpsCtraCd}></Group.Control>
                                {feeImpsCtraCd != "U" && feeImpsCtraCd != "M" && (
                                    <Group.Control
                                        {...form.lpcoFeeDtl.schema.feeImpsCndtCd}
                                        onChange={(v) => {
                                            console.log("VV2", v);
                                            setFeeImpsCndtCd(v);
                                        }}
                                    ></Group.Control>
                                )}{" "}
                                {(feeImpsCtraCd == "U" || feeImpsCtraCd == "M") && (
                                    <Group.Control
                                        {...form.lpcoFeeDtl.schema.feePrgmNm}
                                        required={feeImpsCtraCd != "M"}
                                    ></Group.Control>
                                )}
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.lpcoFeeDtl.schema.srvcFeeCurrCd}></Group.Control>
                                <Group.Control {...form.lpcoFeeDtl.schema.baseFee}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.lpcoFeeDtl.schema.vldStrtDt}></Group.Control>
                                <Group.Control {...form.lpcoFeeDtl.schema.vldEndDt}></Group.Control>
                            </Group.Row>

                            <Group.Row>
                                <Group.Control {...form.lpcoFeeDtl.schema.feeDesc} controlSize={10}></Group.Control>
                            </Group.Row>
                        </Group.Section>
                    </Group.Body>
                </Group>

                {feeImpsCtraCd != "U" && feeImpsCtraCd != "D" && feeImpsCtraCd != "M" && (
                    <Group>
                        <Group.Body>
                            <Group.Title title={"T_CNDT"} titleSize={2}></Group.Title>
                            <Layout direction="row">
                                <Layout.Left></Layout.Left>
                                <Layout.Right></Layout.Right>
                            </Layout>
                            <Grid
                                {...grid.feeCondPerDocList.grid}
                                data={lpcoFeeList} /* fetch.getRpckItmAppList.data?.lpcoFeeList */
                                render={render.grid_feeCondList}
                                // onCellClick={handler.click_Grid_RpckItmAppList}
                                // onRowClick={handler.click_GridRow_RpckItmAppList}
                            />
                        </Group.Body>
                    </Group>
                )}

                {/* GFS Code List !== GFS 코드 목록 (조회) ==! */}

                <Group>
                    <Group.Body>
                        <Group.Title title={"L_GFS_CD_LST"} titleSize={2}></Group.Title>
                        <Layout direction="row">
                            <Layout.Left></Layout.Left>
                            <Layout.Right></Layout.Right>
                        </Layout>
                        <Grid {...grid.gfsCdList.grid} data={gfsCdList} render={render.grid_gfsCdList} />
                    </Group.Body>
                </Group>
            </form>
            <Layout direction="row">
                <Layout.Left>
                    <Button
                        role="list"
                        onClick={() => {
                            navigate(URLS.osci0203022q);
                        }}
                    ></Button>
                </Layout.Left>
                <Layout.Right>
                    <Button
                        role="edit"
                        onClick={() => {
                            navigate(`${URLS.osci0203024s}/${feeImpsCtraId}`);
                        }}
                    ></Button>
                    <Button
                        role="delete"
                        onClick={() => {
                            handler.deleteFeeInfo(feeImpsCtraId);
                        }}
                    ></Button>
                </Layout.Right>
            </Layout>
        </Page>
    );
};
